import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleRatingBadge from 'src/components/simple-rating-badge';
import { useControlTypeLabel } from 'src/pages/assessments/forms/useControlTypeLabel';
import { UNRATED } from 'src/pages/controls/lookupData';

import Link from '@/components/link';
import { useRiskScore } from '@/hooks/useRiskScore';
import type { UseGetTablePropsOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTablePropsWithoutUrlHash } from '@/utils/table/hooks/useGetTablePropsWithoutUrlHash';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumn } from '@/utils/table/utils/dateColumn';
import { complianceMonitoringAssessmentDetailsUrl } from '@/utils/urls';

import type {
  ComplianceRiskAssessmentResultFlatFields,
  ComplianceRiskAssessmentResultRegisterFields,
} from './types';
import { useComplianceRatingLabelledFields } from './useComplianceRatingLabelledFields';

const useGetFieldConfig = (
  onOpenResult: (id: string) => void,
  riskId: string
): TableFields<ComplianceRiskAssessmentResultRegisterFields> => {
  const { t: at } = useTranslation(['common'], {
    keyPrefix: 'complianceMonitoringAssessment',
  });
  const { t: ar } = useTranslation(['common'], {
    keyPrefix: 'assessmentResults',
  });
  const { t: rt } = useTranslation(['common'], {
    keyPrefix: 'ratings',
  });
  const getControlTypeLabel = useControlTypeLabel();
  const riskScores = useRiskScore(riskId);
  const { getByValue: getUncontrolledByValue } = useRating('risk_uncontrolled');
  const { getByValue: getControlledByValue } = useRating('risk_controlled');
  const { getByValue: getLikelihoodByValue } = useRating('likelihood');
  const { getByValue: getImpactByValue } = useRating('impact');
  const { getByValue: statusGetByValue } = useRating('assessment_status');

  return useMemo(
    () => ({
      TestDate: dateColumn(rt('columns.TestDate'), 'TestDate', (item) =>
        onOpenResult(item.Id)
      ),
      ParentTitle: {
        header: rt('columns.ComplianceMonitoringAssessmentTitle'),
        cell: (item) =>
          item.parents.find((p) => p.complianceMonitoringAssessment)
            ?.complianceMonitoringAssessment ? (
            <Link
              variant={'secondary'}
              href={complianceMonitoringAssessmentDetailsUrl(
                item.parents.find((p) => p.complianceMonitoringAssessment)!
                  .complianceMonitoringAssessment!.Id
              )}
            >
              {
                item.parents.find((p) => p.complianceMonitoringAssessment)
                  ?.complianceMonitoringAssessment?.Title
              }
            </Link>
          ) : (
            '-'
          ),
        isRowHeader: true,
      },
      ControlTypeLabelled: {
        header: ar('fields.ControlType'),
        cell: (item) => getControlTypeLabel(item.ControlType),
        sortingField: 'ControlType',
      },
      RatingLabelled: {
        header: rt('columns.Rating'),
        sortingField: 'Rating',
        cell: (item) => {
          if (!riskScores.showScore) {
            const result =
              item.ControlType ===
              Risk_Assessment_Result_Control_Type_Enum.Controlled
                ? getControlledByValue(item.Rating)
                : getUncontrolledByValue(item.Rating);

            return <SimpleRatingBadge rating={result ?? UNRATED} />;
          }

          return '-';
        },
      },
      ImpactLabelled: {
        header: rt('columns.Impact'),
        cell: (item) =>
          item.Impact ? (
            <SimpleRatingBadge rating={getImpactByValue(item.Impact)} />
          ) : (
            '-'
          ),
      },
      LikelihoodLabelled: {
        header: rt('columns.Likelihood'),
        cell: (item) =>
          item.Likelihood ? (
            <SimpleRatingBadge rating={getLikelihoodByValue(item.Likelihood)} />
          ) : (
            '-'
          ),
      },
      StatusLabelled: {
        header: rt('columns.ComplianceMonitoringAssessmentStatus'),
        cell: (item) => {
          const status = item.parents.find(
            (p) => p.complianceMonitoringAssessment
          )?.complianceMonitoringAssessment?.Status;

          return <SimpleRatingBadge rating={statusGetByValue(status)} />;
        },
      },
      CompletionDate: dateColumn(
        at('columns.CompletionDate'),
        'ActualCompletionDate'
      ),
      NextTestDate: dateColumn(at('columns.NextTestDate'), 'NextTestDate'),
    }),
    [
      ar,
      at,
      getControlTypeLabel,
      getControlledByValue,
      getImpactByValue,
      getLikelihoodByValue,
      getUncontrolledByValue,
      onOpenResult,
      riskScores.showScore,
      rt,
      statusGetByValue,
    ]
  );
};

const useGetComplianceRatingTableProps = (
  riskId: string,
  onOpenResult: (id: string) => void,
  records: ComplianceRiskAssessmentResultFlatFields[] | undefined
): UseGetTablePropsOptions<ComplianceRiskAssessmentResultRegisterFields> => {
  const labelledFields = useComplianceRatingLabelledFields(riskId, records);
  const fields = useGetFieldConfig(onOpenResult, riskId);

  return {
    customAttributeFormIds: [
      'risk_uncontrolled_second_line_result',
      'risk_controlled_second_line_result',
    ],
    data: labelledFields,
    enableFiltering: true,
    entityLabel: 'rating',
    defaultSortingState: {
      sortingColumn: 'TestDate',
      sortingDirection: 'desc',
    },
    initialColumns: [
      'TestDate',
      'ParentTitle',
      'ControlTypeLabelled',
      'RatingLabelled',
      'ImpactLabelled',
      'LikelihoodLabelled',
      'StatusLabelled',
    ],
    preferencesStorageKey: 'ComplianceRiskRatingRegisterTable-Preferences',
    fields,
  };
};

export const useGetCollectionTableProps = (
  riskId: string,
  onOpenResult: (id: string) => void,
  records: ComplianceRiskAssessmentResultFlatFields[] | undefined
): TablePropsWithActions<ComplianceRiskAssessmentResultRegisterFields> => {
  const props = useGetComplianceRatingTableProps(riskId, onOpenResult, records);

  return useGetTablePropsWithoutUrlHash(props);
};
